import React from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Box, Drawer, useMediaQuery, useTheme } from "@mui/material";
import Header from "./components/layout/Header";
import Sidebar from "./components/layout/Sidebar";
import Footer from "./components/layout/Footer";
import ProtectedRoute from "./components/ProtectedRoute";
import { SidebarProvider, useSidebar } from "./contexts/SidebarContext";
// TODO: Privileges will be implemented later
import Home from "./pages/Home";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import ApproverDashboard from "./pages/ApproverDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import ClaimPortalDashboard from "./pages/ClaimPortalDashboard";
import DomesticReports from "./pages/DomesticReports";
import SrDetailDump from "./pages/SrDetailDump";
import ComplaintReports from "./pages/ComplaintReports";
import StatusReports from "./pages/StatusReports";
import Settings from "./pages/Settings";
import Complaints from "./pages/Complaints";
import BCMServiceRequestList from "./pages/BCMServiceRequestList";
import AdminServiceRequestList from "./pages/AdminServiceRequestList";
import ServiceRequestActivityLog from "./pages/ServiceRequestActivityLog";
import ApprovalSR from "./pages/ApprovalSR";
import ViewServiceRequest from "./pages/ViewServiceRequest";
import ViewApproverServiceRequest from "./pages/ViewApproverServiceRequest";
import RaiseComplaint from "./pages/RaiseComplaint";
import RaiseComplaintChecklist from "./pages/RaiseComplaintChecklist";
import MyDraftServiceRequests from "./pages/MyDraftServiceRequests";
import UserManagement from "./pages/UserManagement";
import AddEditUser from "./pages/AddEditUser";
import ProductMasterManagement from "./pages/ProductMasterManagement";
import AddEditProductMaster from "./pages/AddEditProductMaster";
import ProductTypeMasterManagement from "./pages/ProductTypeMasterManagement";
import AddEditProductTypeMaster from "./pages/AddEditProductTypeMaster";
import CategoryMasterManagement from "./pages/CategoryMasterManagement";
import AddEditCategoryMaster from "./pages/AddEditCategoryMaster";
import PlantMasterManagement from "./pages/PlantMasterManagement";
import AddEditPlantMaster from "./pages/AddEditPlantMaster";
import BusinessVerticalMasterManagement from "./pages/BusinessVerticalMasterManagement";
import AddEditBusinessVerticalMaster from "./pages/AddEditBusinessVerticalMaster";
import CountryMasterManagement from "./pages/CountryMasterManagement";
import AddEditCountryMaster from "./pages/AddEditCountryMaster";
import StateMasterManagement from "./pages/StateMasterManagement";
import AddEditStateMaster from "./pages/AddEditStateMaster";
import RegionMasterManagement from "./pages/RegionMasterManagement";
import AddEditRegionMaster from "./pages/AddEditRegionMaster";
import ZoneMasterManagement from "./pages/ZoneMasterManagement";
import AddEditZoneMaster from "./pages/AddEditZoneMaster";
import TerritoryMasterManagement from "./pages/TerritoryMasterManagement";
import AddEditTerritoryMaster from "./pages/AddEditTerritoryMaster";
import RDCMasterManagement from "./pages/RDCMasterManagement";
import AddEditRDCMaster from "./pages/AddEditRDCMaster";
import ComplaintCategoryMasterManagement from "./pages/ComplaintCategoryMasterManagement";
import AddEditComplaintCategoryMaster from "./pages/AddEditComplaintCategoryMaster";
import ComponentMasterManagement from "./pages/ComponentMasterManagement";
import AddEditComponentMaster from "./pages/AddEditComponentMaster";
import CustomerTypeManagement from "./pages/CustomerTypeManagement";
import PrivilegeManagement from "./pages/PrivilegeManagement";
import AddEditPrivilege from "./pages/AddEditPrivilege";
import ChecklistDocumentManagement from "./pages/ChecklistDocumentManagement";
import ChecklistTemplateManagement from "./pages/ChecklistTemplateManagement";
import AddEditChecklistTemplate from "./pages/AddEditChecklistTemplate";
import ChecklistQuestionTreeManagement from "./pages/ChecklistQuestionTreeManagement";
import AddEditChecklistQuestion from "./pages/AddEditChecklistQuestion";
import DynamicApprovalConfiguration from "./pages/DynamicApprovalConfiguration";
import DynamicApprovalConfigurationManagement from "./pages/DynamicApprovalConfigurationManagement";
import DynamicApprovalConfigurationLevelSetup from "./pages/DynamicApprovalConfigurationLevelSetup";
import SLATatConfiguration from "./pages/SLATatConfiguration";
import SLATatConfigurationManagement from "./pages/SLATatConfigurationManagement";
import EmailTemplateManagement from "./pages/EmailTemplateManagement";
import AddEditEmailTemplate from "./pages/AddEditEmailTemplate";
import DynamicApprovalValueConfigurationLevelSetup from "./pages/DynamicApprovalValueConfigurationLevelSetup";
import { isAuthenticated, hasRole } from "./utils/auth";
import { SessionProvider } from "./contexts/SessionContext";
import { ApiConnectivityProvider } from "./contexts/ApiConnectivityContext";
import "./App.css";

function AppContent() {
  const location = useLocation();
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const authenticated = isAuthenticated();
  const isAV = hasRole("av");
  const isMobile = useMediaQuery("(max-width: 768px)");
  const theme = useTheme();

  // Disable browser auto scroll restoration and smooth scroll on all pages to prevent jerking
  React.useEffect(() => {
    const prev = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = prev;
    };
  }, []);

  // When mobile sidebar is open, prevent background page scroll.
  React.useEffect(() => {
    if (!authenticated) return;
    if (!sidebarOpen || !isMobile) return;

    const prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prevBodyOverflow;
    };
  }, [authenticated, sidebarOpen, isMobile]);

  const handleMenuClick = () => {
    toggleSidebar();
  };

  // Helper function to check if AV user is trying to access restricted route
  const isRestrictedRouteForAV = (path) => {
    if (!isAV) return false;
    // AV users can only access: /complaints, /approval-sr, /complaints/:id
    const allowedPaths = ["/complaints", "/approval-sr"];
    const isAllowed = allowedPaths.some(
      (allowed) => path === allowed || path.startsWith("/complaints/")
    );
    return !isAllowed;
  };

  return (
    <div className="App">
      <Header onMenuClick={handleMenuClick} sidebarOpen={sidebarOpen} />
      <Box sx={{ display: "flex", mt: "64px", pb: "80px" }}>
        {authenticated && (
          <>
            <Drawer
              variant="persistent"
              open={sidebarOpen}
              sx={{
                width: sidebarOpen ? 250 : 0,
                flexShrink: 0,
                "& .MuiDrawer-paper": {
                  width: 250,
                  boxSizing: "border-box",
                  position: "fixed",
                  top: "64px",
                  left: 0,
                  height: "calc(100vh - 64px)",
                  borderRight: "1px solid #e0e0e0",
                },
              }}
            >
              <Sidebar />
            </Drawer>

            {sidebarOpen && isMobile && (
              <Box
                sx={{
                  position: "fixed",
                  top: "64px",
                  left: "250px",
                  right: 0,
                  bottom: 0,
                  width: "calc(100% - 250px)",
                  height: "100%",
                  backgroundColor: "#000",
                  opacity: 0.5,
                  // Keep this overlay under the Drawer, but above page content.
                  zIndex: theme.zIndex.drawer - 1,
                }}
              />
            )}
          </>
        )}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            backgroundColor: "#f5f5f5",
            minHeight: "calc(100vh - 64px)",
            width: sidebarOpen ? "calc(100% - 250px)" : "100%",
            marginBottom: authenticated ? "60px" : "0px",
            transition: "margin-left 0.3s, width 0.3s",
          }}
        >
          <Routes>
            {/* TODO: Privileges will be implemented later - all routes accessible to authenticated users for now */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  {isRestrictedRouteForAV(location.pathname) ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <Home />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  {isRestrictedRouteForAV("/dashboard") ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <Dashboard />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/approver-dashboard"
              element={
                <ProtectedRoute>
                  {isRestrictedRouteForAV("/approver-dashboard") ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <ApproverDashboard />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin-dashboard"
              element={
                <ProtectedRoute>
                  {isRestrictedRouteForAV("/admin-dashboard") ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <AdminDashboard />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/claim-portal-dashboard"
              element={
                <ProtectedRoute requiredPrivileges={["CLAIM_PORTAL_DASHBOARD"]}>
                  {isRestrictedRouteForAV("/claim-portal-dashboard") ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <ClaimPortalDashboard />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/domestic"
              element={
                <ProtectedRoute requiredPrivileges={["DOMESTIC_REPORTS"]}>
                  {isRestrictedRouteForAV("/reports/domestic") ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <DomesticReports />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/sr-detail-dump"
              element={
                <ProtectedRoute requiredPrivileges={["SR_DETAIL_DUMP"]}>
                  {isRestrictedRouteForAV("/reports/sr-detail-dump") ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <SrDetailDump />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/complaints"
              element={
                <ProtectedRoute requiredPrivileges={["REPORT_COMPLAINT"]}>
                  {isRestrictedRouteForAV("/reports/complaints") ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <ComplaintReports />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports/status"
              element={
                <ProtectedRoute requiredPrivileges={["REPORT_STATUS"]}>
                  {isRestrictedRouteForAV("/reports/status") ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <StatusReports />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute requiredPrivileges={["SETTINGS_MANAGE"]}>
                  {isRestrictedRouteForAV("/settings") ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <Settings />
                  )}
                </ProtectedRoute>
              }
            />
            {/* TODO: Will add requiredPrivileges={['COMPLAINT_VIEW']} later */}
            <Route
              path="/complaints"
              element={
                <ProtectedRoute>
                  <Complaints />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bcm-service-requests"
              element={
                <ProtectedRoute>
                  <BCMServiceRequestList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin-service-requests"
              element={
                <ProtectedRoute>
                  <AdminServiceRequestList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sr-activity-log"
              element={
                <ProtectedRoute>
                  <ServiceRequestActivityLog />
                </ProtectedRoute>
              }
            />
            <Route
              path="/approval-sr"
              element={
                <ProtectedRoute>
                  <ApprovalSR />
                </ProtectedRoute>
              }
            />
            <Route
              path="/complaints/:id"
              element={
                <ProtectedRoute>
                  <ViewServiceRequest />
                </ProtectedRoute>
              }
            />
            <Route
              path="/view-approver-sr/:id"
              element={
                <ProtectedRoute>
                  <ViewApproverServiceRequest />
                </ProtectedRoute>
              }
            />
            {/* TODO: Will add requiredPrivileges={['COMPLAINT_CREATE']} later */}
            <Route
              path="/raise-complaint"
              element={
                <ProtectedRoute>
                  {isRestrictedRouteForAV("/raise-complaint") ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <RaiseComplaint />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/raise-complaint-checklist"
              element={
                <ProtectedRoute>
                  {isRestrictedRouteForAV("/raise-complaint-checklist") ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <RaiseComplaintChecklist />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-draft-service-requests"
              element={
                <ProtectedRoute>
                  {isRestrictedRouteForAV("/my-draft-service-requests") ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <MyDraftServiceRequests />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  {isRestrictedRouteForAV("/users") ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <UserManagement />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/add"
              element={
                <ProtectedRoute>
                  {isRestrictedRouteForAV("/users/add") ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <AddEditUser />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/edit/:userIdCode"
              element={
                <ProtectedRoute>
                  {isRestrictedRouteForAV("/users/edit") ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <AddEditUser />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute>
                  <ProductMasterManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/add"
              element={
                <ProtectedRoute>
                  <AddEditProductMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/edit/:productCode"
              element={
                <ProtectedRoute>
                  <AddEditProductMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-types"
              element={
                <ProtectedRoute>
                  <ProductTypeMasterManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-types/add"
              element={
                <ProtectedRoute>
                  <AddEditProductTypeMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-types/edit/:productTypeCode"
              element={
                <ProtectedRoute>
                  <AddEditProductTypeMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-categories"
              element={
                <ProtectedRoute>
                  <CategoryMasterManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-categories/add"
              element={
                <ProtectedRoute>
                  <AddEditCategoryMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-categories/edit/:catgCode"
              element={
                <ProtectedRoute>
                  <AddEditCategoryMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plants"
              element={
                <ProtectedRoute>
                  <PlantMasterManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plants/add"
              element={
                <ProtectedRoute>
                  <AddEditPlantMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/plants/edit/:plantCode"
              element={
                <ProtectedRoute>
                  <AddEditPlantMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-verticals"
              element={
                <ProtectedRoute>
                  <BusinessVerticalMasterManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-verticals/add"
              element={
                <ProtectedRoute>
                  <AddEditBusinessVerticalMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-verticals/edit/:bvCode"
              element={
                <ProtectedRoute>
                  <AddEditBusinessVerticalMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/product-verticals/view/:bvCode"
              element={
                <ProtectedRoute>
                  <AddEditBusinessVerticalMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/countries"
              element={
                <ProtectedRoute>
                  <CountryMasterManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/countries/add"
              element={
                <ProtectedRoute>
                  <AddEditCountryMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/countries/edit/:countryCode"
              element={
                <ProtectedRoute>
                  <AddEditCountryMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/states"
              element={
                <ProtectedRoute>
                  <StateMasterManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/states/add"
              element={
                <ProtectedRoute>
                  <AddEditStateMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/states/edit/:stateCode"
              element={
                <ProtectedRoute>
                  <AddEditStateMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/regions"
              element={
                <ProtectedRoute>
                  <RegionMasterManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/regions/add"
              element={
                <ProtectedRoute>
                  <AddEditRegionMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/regions/edit/:regionCode"
              element={
                <ProtectedRoute>
                  <AddEditRegionMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/zones"
              element={
                <ProtectedRoute>
                  <ZoneMasterManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/zones/add"
              element={
                <ProtectedRoute>
                  <AddEditZoneMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/zones/edit/:zoneCode"
              element={
                <ProtectedRoute>
                  <AddEditZoneMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/territories"
              element={
                <ProtectedRoute>
                  <TerritoryMasterManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/territories/add"
              element={
                <ProtectedRoute>
                  <AddEditTerritoryMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/territories/edit/:territoryCode"
              element={
                <ProtectedRoute>
                  <AddEditTerritoryMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rdcs"
              element={
                <ProtectedRoute>
                  <RDCMasterManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rdcs/add"
              element={
                <ProtectedRoute>
                  <AddEditRDCMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rdcs/edit/:rdcCode"
              element={
                <ProtectedRoute>
                  <AddEditRDCMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/component-categories"
              element={
                <ProtectedRoute>
                  <ComplaintCategoryMasterManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/component-categories/add"
              element={
                <ProtectedRoute>
                  <AddEditComplaintCategoryMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/component-categories/edit/:compCatgCode"
              element={
                <ProtectedRoute>
                  <AddEditComplaintCategoryMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/components"
              element={
                <ProtectedRoute>
                  <ComponentMasterManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/components/add"
              element={
                <ProtectedRoute>
                  <AddEditComponentMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/components/edit/:componentCode"
              element={
                <ProtectedRoute>
                  <AddEditComponentMaster />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-types"
              element={
                <ProtectedRoute>
                  {isRestrictedRouteForAV("/customer-types") ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <CustomerTypeManagement />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/privileges"
              element={
                <ProtectedRoute>
                  {isRestrictedRouteForAV("/privileges") ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <PrivilegeManagement />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/privileges/add"
              element={
                <ProtectedRoute>
                  {isRestrictedRouteForAV("/privileges/add") ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <AddEditPrivilege />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/privileges/edit/:privCode"
              element={
                <ProtectedRoute>
                  {isRestrictedRouteForAV("/privileges/edit") ? (
                    <Navigate to="/complaints" replace />
                  ) : (
                    <AddEditPrivilege />
                  )}
                </ProtectedRoute>
              }
            />
            <Route
              path="/checklist-documents"
              element={
                <ProtectedRoute>
                  <ChecklistDocumentManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checklist-templates"
              element={
                <ProtectedRoute>
                  <ChecklistTemplateManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checklist-templates/add"
              element={
                <ProtectedRoute>
                  <AddEditChecklistTemplate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checklist-templates/edit/:templateCode"
              element={
                <ProtectedRoute>
                  <AddEditChecklistTemplate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checklist-questions/add"
              element={
                <ProtectedRoute>
                  <AddEditChecklistQuestion />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checklist-questions/edit/:questionCode"
              element={
                <ProtectedRoute>
                  <AddEditChecklistQuestion />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checklist-questions/tree"
              element={
                <ProtectedRoute>
                  <ChecklistQuestionTreeManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dynamic-approval-configuration"
              element={
                <ProtectedRoute>
                  <DynamicApprovalConfigurationManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sla-tat-configuration"
              element={
                <ProtectedRoute>
                  <SLATatConfigurationManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sla-tat-configuration/add"
              element={
                <ProtectedRoute>
                  <SLATatConfiguration />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sla-tat-configuration/edit/:workflowCode"
              element={
                <ProtectedRoute>
                  <SLATatConfiguration />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sla-tat-configuration/configure/:workflowCode"
              element={
                <ProtectedRoute>
                  <SLATatConfiguration />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sla-tat-configuration/levels/:workflowCode"
              element={
                <ProtectedRoute>
                  <DynamicApprovalConfigurationLevelSetup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sla-tat-configuration-level-setup"
              element={
                <ProtectedRoute>
                  <DynamicApprovalConfigurationLevelSetup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sla-tat-configuration-level-setup/:workflowCode"
              element={
                <ProtectedRoute>
                  <DynamicApprovalConfigurationLevelSetup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dynamic-approval-configuration/add"
              element={
                <ProtectedRoute>
                  <DynamicApprovalConfiguration />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dynamic-approval-configuration/edit/:workflowCode"
              element={
                <ProtectedRoute>
                  <DynamicApprovalConfiguration />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dynamic-approval-configuration/configure/:workflowCode"
              element={
                <ProtectedRoute>
                  <DynamicApprovalConfiguration />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dynamic-approval-configuration/levels/:workflowCode"
              element={
                <ProtectedRoute>
                  <DynamicApprovalConfigurationLevelSetup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dynamic-approval-configuration-level-setup"
              element={
                <ProtectedRoute>
                  <DynamicApprovalConfigurationLevelSetup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dynamic-approval-configuration-level-setup/:workflowCode"
              element={
                <ProtectedRoute>
                  <DynamicApprovalConfigurationLevelSetup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dynamic-approval-value-configuration-level-setup"
              element={
                <ProtectedRoute>
                  <DynamicApprovalValueConfigurationLevelSetup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dynamic-approval-value-configuration-level-setup/:workflowCode"
              element={
                <ProtectedRoute>
                  <DynamicApprovalValueConfigurationLevelSetup />
                </ProtectedRoute>
              }
            />
            <Route
              path="/email-templates"
              element={
                <ProtectedRoute>
                  <EmailTemplateManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/email-templates/add"
              element={
                <ProtectedRoute>
                  <AddEditEmailTemplate />
                </ProtectedRoute>
              }
            />
            <Route
              path="/email-templates/edit/:templateCode"
              element={
                <ProtectedRoute>
                  <AddEditEmailTemplate />
                </ProtectedRoute>
              }
            />
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route
              path="*"
              element={
                isAV ? (
                  <Navigate to="/complaints" replace />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
          </Routes>
        </Box>
      </Box>
      {authenticated && <Footer sidebarOpen={sidebarOpen} />}
    </div>
  );
}

function App() {
  const authenticated = isAuthenticated();

  // Always show the login page first when not authenticated (any path)
  if (!authenticated) {
    return (
      <div className="App">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </div>
    );
  }

  return (
    <ApiConnectivityProvider enabled>
      <SessionProvider enabled>
        <SidebarProvider>
          <AppContent />
        </SidebarProvider>
      </SessionProvider>
    </ApiConnectivityProvider>
  );
}

export default App;
